import { ReactNode, Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore, selectHasIdentity, selectHasTemporaryAccess } from './store/authStore';
import { useTemporaryMembershipAutoConnect } from './hooks/useTemporaryMembershipAutoConnect';
import { authAPI } from './api/client';
import { RegisterPage } from './components/RegisterPage';
import { ConfirmDialog } from './components/ConfirmDialog';
import { CallsPage } from './pages/CallsPage';
import { tryUnlockCallSounds, useCallListeners } from './hooks/useCall';
import { useDirectFileTransferListeners } from './hooks/useDirectFileTransferListeners';
import { useVaultStore } from './store/vaultStore';
import { IncomingCallModal } from './components/IncomingCallModal';
import { IncomingCallPassiveBanner } from './components/IncomingCallPassiveBanner';
import { DirectFileTransferOverlay } from './components/DirectFileTransferOverlay';
import { useCallStore } from './store/callStore';
import { normalizeServerUrl, serverKeyFromServerUrl } from './utils/serverKey';
import { useI18n } from './i18n';
import { initializeMessaging } from './services/messages';
import { initializeCallHistorySync } from './services/callHistorySync';
import { initializeSystemEvents } from './services/systemEvents';
import { initializeTemporaryTrustedAccessEvents } from './services/temporaryTrustedAccessEvents';
import { initializeMessageArchiveWriter } from './services/messageArchiveWriter';
import {
  PRESENCE_HEARTBEAT_INTERVAL_MS,
  PRESENCE_STATUS_REFRESH_INTERVAL_MS,
  refreshContactPresenceStatuses,
  sendPresenceHeartbeatAllCircles
} from './services/presence';
import { syncAllServersSequentiallyWithResult } from './services/multiServerSync';
import { enablePushNotificationsOnAllKnownServers, isPushSupported } from './services/push';
import { wsClient } from './services/websocket';
import { showToast } from './utils/toast';
import { requestConfirmation } from './utils/feedback';
import { clearLocalBrowserProfile } from './utils/localProfileReset';
import { deleteRemovedCircleLocalCopy } from './utils/removedCircleAccess';
import { verifyLocalCircleDataDeletionAuthorization } from './crypto/localCircleDataDeletionAuthorization';
import type { DeviceLocalCircleDataDeletionAuthorization } from '@shared/types';
import { webrtcManager } from './services/webrtc';
import {
  clearMobileRefreshGrant,
  exchangeMobileHandoffViaCentral,
  fetchMobileHandoffStatusViaCentral,
  getKnownServerKeys,
  loadMobileRefreshGrant,
  type MobileDeliveryTokenRegistrationProgress,
  shouldRefreshDeliveryTokenForKnownServers,
  redeemMobileRefreshGrantViaCentral,
  registerMobileDeliveryTokenOnAllKnownServers,
  saveMobileRefreshGrant,
  updateMobileServerTokenValidity
} from './services/mobilePush';
import { useCallHistoryStore } from './store/callHistoryStore';
import { consumeMobileHandoffFromBridge, hasWebViewMediaPermissions, isWebViewMode } from './utils/webviewBridge';
import { requestWebViewMediaPermissions } from './utils/webviewMediaPermissions';
import { consumeMasterKeyNudge, shouldShowMasterKeyNudge } from './services/masterKeyNudge';
import { startDeviceRegistrationSelfHeal } from './services/deviceRegistrationSelfHeal';
import { consumeCallReturnPath } from './call/callNavigationState';
import { canNavigateBackInApp, getDeepRouteParentTab } from './utils/navigationBack';
import { DesktopWorkspaceShell, type WorkspaceSection } from './components/DesktopWorkspaceShell';
import { useMediaQuery } from './hooks/useMediaQuery';

const RouteLoading = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary-600 border-r-transparent"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

const LazyKeyManagementPage = lazy(() =>
  import('./components/KeyManagementPage').then((m) => ({ default: m.KeyManagementPage }))
);
const LazyAddContactPage = lazy(() =>
  import('./components/AddContactPage').then((m) => ({ default: m.AddContactPage }))
);
const LazyCallPage = lazy(() =>
  import('./components/CallPage').then((m) => ({ default: m.CallPage }))
);
const LazyMyStatusPage = lazy(() =>
  import('./components/MyStatusPage').then((m) => ({ default: m.MyStatusPage }))
);
const LazyDeviceManagementPage = lazy(() =>
  import('./components/DeviceManagementPage').then((m) => ({ default: m.DeviceManagementPage }))
);

const LazyContactsPage = lazy(() =>
  import('./pages/ContactsPage').then((m) => ({ default: m.ContactsPage }))
);
const LazyMessagesPage = lazy(() =>
  import('./pages/MessagesPage').then((m) => ({ default: m.MessagesPage }))
);
const LazyShareTargetPage = lazy(() =>
  import('./pages/ShareTargetPage').then((m) => ({ default: m.ShareTargetPage }))
);
const LazyNewChatPage = lazy(() =>
  import('./pages/NewChatPage').then((m) => ({ default: m.NewChatPage }))
);
const LazyMessageThreadPage = lazy(() =>
  import('./pages/MessageThreadPage').then((m) => ({ default: m.MessageThreadPage }))
);
const LazyGroupThreadPage = lazy(() =>
  import('./pages/GroupThreadPage').then((m) => ({ default: m.GroupThreadPage }))
);
const LazyGroupInviteLinkPage = lazy(() =>
  import('./pages/GroupInviteLinkPage').then((m) => ({ default: m.GroupInviteLinkPage }))
);
const LazyContactDetailPage = lazy(() =>
  import('./pages/ContactDetailPage').then((m) => ({ default: m.ContactDetailPage }))
);
const LazyServersPage = lazy(() =>
  import('./pages/ServersPage').then((m) => ({ default: m.CirclesPage }))
);
const LazyServerDetailPage = lazy(() =>
  import('./pages/ServerDetailPage').then((m) => ({ default: m.CircleDetailPage }))
);
const LazyCircleSettingsPage = lazy(() =>
  import('./pages/CircleSettingsPage').then((m) => ({ default: m.CircleSettingsPage }))
);
const LazyMessageArchivePage = lazy(() =>
  import('./pages/MessageArchivePage').then((m) => ({ default: m.MessageArchivePage }))
);
const LazyMessageArchiveManagementPage = lazy(() =>
  import('./pages/MessageArchiveManagementPage').then((m) => ({ default: m.MessageArchiveManagementPage }))
);
const LazySettingsPage = lazy(() =>
  import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);
const LazyAdvancedSettingsPage = lazy(() =>
  import('./pages/AdvancedSettingsPage').then((m) => ({ default: m.AdvancedSettingsPage }))
);
const LazyChatSettingsPage = lazy(() =>
  import('./pages/ChatSettingsPage').then((m) => ({ default: m.ChatSettingsPage }))
);
const LazyShareContactPage = lazy(() =>
  import('./pages/ShareContactPage').then((m) => ({ default: m.ShareContactPage }))
);
const LazyAddServerPage = lazy(() =>
  import('./pages/AddServerPage').then((m) => ({ default: m.JoinCirclePage }))
);
const LazyServerManagementPage = lazy(() =>
  import('./pages/ServerManagementPage').then((m) => ({ default: m.ServerManagementPage }))
);
const LazyRecoverIdentityPage = lazy(() =>
  import('./pages/RecoverIdentityPage').then((m) => ({ default: m.RecoverIdentityPage }))
);
const LazyMasterKeySetupPage = lazy(() =>
  import('./pages/MasterKeySetupPage').then((m) => ({ default: m.MasterKeySetupPage }))
);
const LazyMasterKeyRequestPage = lazy(() =>
  import('./pages/MasterKeyRequestPage').then((m) => ({ default: m.MasterKeyRequestPage }))
);
const LazyMasterKeyApprovePage = lazy(() =>
  import('./pages/MasterKeyApprovePage').then((m) => ({ default: m.MasterKeyApprovePage }))
);
const LazyMasterKeyConflictPage = lazy(() =>
  import('./pages/MasterKeyConflictPage').then((m) => ({ default: m.MasterKeyConflictPage }))
);
const LazyGuestLinksPage = lazy(() =>
  import('./pages/GuestLinksPage').then((m) => ({ default: m.GuestLinksPage }))
);
const LazyProfileReadyPage = lazy(() =>
  import('./pages/ProfileReadyPage').then((m) => ({ default: m.ProfileReadyPage }))
);
const LazyConnectProfilePage = lazy(() =>
  import('./pages/ConnectProfilePage').then((m) => ({ default: m.ConnectProfilePage }))
);
const LazyConnectProfileImportPage = lazy(() =>
  import('./pages/ConnectProfileImportPage').then((m) => ({ default: m.ConnectProfileImportPage }))
);
const LazyConnectProfileDevicePage = lazy(() =>
  import('./pages/ConnectProfileDevicePage').then((m) => ({ default: m.ConnectProfileDevicePage }))
);
const LazyCreateCircleEntryPage = lazy(() =>
  import('./pages/CreateCircleEntryPage').then((m) => ({ default: m.CreateCircleEntryPage }))
);
const LazyInviteCirclesPage = lazy(() =>
  import('./pages/InviteCirclesPage').then((m) => ({ default: m.InviteCirclesPage }))
);
const LazyCreateCirclePage = lazy(() =>
  import('./pages/CreateCirclePage').then((m) => ({ default: m.CreateCirclePage }))
);
const LazyAddDeviceFlowPage = lazy(() =>
  import('./pages/AddDeviceFlowPage').then((m) => ({ default: m.AddDeviceFlowPage }))
);
const LazyCallLinkPage = lazy(() =>
  import('./pages/CallLinkPage').then((m) => ({ default: m.CallLinkPage }))
);
const LazyDirectGuestLinkPage = lazy(() =>
  import('./pages/DirectGuestLinkPage').then((m) => ({ default: m.DirectGuestLinkPage }))
);
const LazyOpenLinkPage = lazy(() =>
  import('./pages/OpenLinkPage').then((m) => ({ default: m.OpenLinkPage }))
);
const LazyUniversalQrScannerPage = lazy(() =>
  import('./pages/UniversalQrScannerPage').then((m) => ({ default: m.UniversalQrScannerPage }))
);
const LazyFirstRunPage = lazy(() =>
  import('./pages/FirstRunPage').then((m) => ({ default: m.FirstRunPage }))
);
const LazyPendingRegistrationPage = lazy(() =>
  import('./pages/PendingRegistrationPage').then((m) => ({ default: m.PendingRegistrationPage }))
);
const LazySelfHostedCreateCirclePage = lazy(() =>
  import('./pages/SelfHostedCreateCirclePage').then((m) => ({ default: m.SelfHostedCreateCirclePage }))
);
const LazyTemporaryAccessPage = lazy(() =>
  import('./pages/TemporaryAccessPage').then((m) => ({ default: m.TemporaryAccessPage }))
);
const LazyTemporaryCirclePage = lazy(() =>
  import('./pages/TemporaryCirclePage').then((m) => ({ default: m.TemporaryCirclePage }))
);
const LazyTemporaryGroupChatPage = lazy(() =>
  import('./pages/TemporaryGroupChatPage').then((m) => ({ default: m.TemporaryGroupChatPage }))
);
const LazyTemporaryTrustedAccessRequestsPage = lazy(() =>
  import('./pages/TemporaryTrustedAccessRequestsPage').then((m) => ({ default: m.TemporaryTrustedAccessRequestsPage }))
);
const LazyRestoreFromBackupPage = lazy(() =>
  import('./pages/RestoreFromBackupPage').then((m) => ({ default: m.RestoreFromBackupPage }))
);
const LazyRecoverCirclesFromBackupPage = lazy(() =>
  import('./pages/RecoverCirclesFromBackupPage').then((m) => ({ default: m.RecoverCirclesFromBackupPage }))
);

const DESKTOP_WORKSPACE_QUERY = '(min-width: 1024px)';

function MessagesWorkspaceRoute({ mobile, detail }: { mobile: ReactNode; detail?: ReactNode }) {
  const isDesktopWorkspace = useMediaQuery(DESKTOP_WORKSPACE_QUERY);
  const { t } = useI18n();

  if (!isDesktopWorkspace) return <>{mobile}</>;

  return (
    <DesktopWorkspaceShell
      activeSection="messages"
      master={<LazyMessagesPage />}
      detail={detail}
      emptyTitle={t('workspace.messagesEmptyTitle')}
      emptyDescription={t('workspace.messagesEmptyDescription')}
    />
  );
}

function SectionWorkspaceRoute({
  section,
  master,
  mobile,
  detail,
  emptyTitle,
  emptyDescription
}: {
  section: WorkspaceSection;
  master: ReactNode;
  mobile: ReactNode;
  detail?: ReactNode;
  emptyTitle: string;
  emptyDescription: string;
}) {
  const isDesktopWorkspace = useMediaQuery(DESKTOP_WORKSPACE_QUERY);

  if (!isDesktopWorkspace) return <>{mobile}</>;

  return (
    <DesktopWorkspaceShell
      activeSection={section}
      master={master}
      detail={detail}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
    />
  );
}

function KeyManagementWorkspaceRoute() {
  const isDesktopWorkspace = useMediaQuery(DESKTOP_WORKSPACE_QUERY);
  const hasIdentity = useAuthStore(selectHasIdentity);
  const { t } = useI18n();

  if (!isDesktopWorkspace || !hasIdentity) return <LazyKeyManagementPage />;

  return (
    <DesktopWorkspaceShell
      activeSection="settings"
      master={<LazySettingsPage />}
      detail={<LazyKeyManagementPage />}
      emptyTitle={t('workspace.settingsEmptyTitle')}
      emptyDescription={t('workspace.settingsEmptyDescription')}
    />
  );
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const hasIdentity = useAuthStore(selectHasIdentity);
  const hasTemporaryAccess = useAuthStore(selectHasTemporaryAccess);
  return (hasIdentity || hasTemporaryAccess) ? <>{children}</> : <Navigate to="/start" />;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const hasIdentity = useAuthStore(selectHasIdentity);
  return !hasIdentity ? <>{children}</> : <Navigate to="/" />;
}

type MobileHandoffDialogState = {
  title: string;
  description: string;
  details?: string[];
  variant: 'success' | 'error';
};

type MobileHandoffPendingState = {
  stage: 'manual' | 'exchange' | 'register';
  title: string;
  detail?: string;
  startedAt: number;
  handoffId?: string;
  handoffCode?: string;
  centralBaseUrl?: string;
  observedCentralStatus?: string;
};

type StoredMobileHandoffParams = {
  handoffId: string;
  handoffCode: string;
  manualDebugMode: boolean;
  manualDebugStart: boolean;
};

const MOBILE_HANDOFF_STORAGE_KEY = 'circlus.mobile_handoff.pending';

function summarizeServerKey(serverKey: string): string {
  if (!serverKey) return 'unknown server';
  try {
    const url = new URL(serverKey);
    return url.host || serverKey;
  } catch {
    return serverKey.replace(/^https?:\/\//, '');
  }
}

function formatMobileHandoffProgress(progress: MobileDeliveryTokenRegistrationProgress): string {
  const serverLabel = summarizeServerKey(progress.serverKey);

  if (progress.stage === 'starting') {
    return `Updating notification access on ${progress.completed + 1}/${progress.total}: ${serverLabel}`;
  }

  if (progress.stage === 'error') {
    return `Still needs attention: ${serverLabel}`;
  }

  return `Updated ${progress.completed}/${progress.total} connected locations.`;
}

function describeExchangePending(
  startedAt: number,
  observedCentralStatus?: string
): string {
  if (observedCentralStatus === 'exchanged_by_web') {
    return 'The mobile app already confirmed the reconnect request. Finishing the browser step now.';
  }
  if (observedCentralStatus === 'expired') {
    return 'This reconnect request expired before it could finish.';
  }

  const elapsedMs = Math.max(0, Date.now() - startedAt);

  if (elapsedMs >= 10000) {
    return 'This is taking longer than usual. Keep this tab open while the reconnect finishes.';
  }
  if (elapsedMs >= 5000) {
    return 'Still waiting for the mobile reconnect to finish.';
  }
  if (elapsedMs >= 1500) {
    return 'Reconnect request sent. Waiting for confirmation from the mobile notification service.';
  }
  return 'Preparing a secure reconnect request for this browser.';
}

function humanizeMobileHandoffError(error: string): string {
  const value = String(error || '').trim();
  if (!value) return 'Could not finish the reconnect on this server.';
  if (/Failed to register delivery token/i.test(value)) {
    return 'Could not finish reconnecting notifications on this server.';
  }
  if (/Missing active delivery token/i.test(value)) {
    return 'The mobile app has not provided a fresh notification token yet.';
  }
  if (/timed out/i.test(value)) {
    return 'The server did not respond in time.';
  }
  return value;
}

function loadStoredMobileHandoffParams(): StoredMobileHandoffParams | null {
  try {
    const raw = window.sessionStorage.getItem(MOBILE_HANDOFF_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredMobileHandoffParams>;
    const handoffId = String(parsed.handoffId || '').trim();
    const handoffCode = String(parsed.handoffCode || '').trim();
    if (!handoffId || !handoffCode) return null;
    return {
      handoffId,
      handoffCode,
      manualDebugMode: parsed.manualDebugMode === true,
      manualDebugStart: parsed.manualDebugStart === true
    };
  } catch {
    return null;
  }
}

function saveStoredMobileHandoffParams(params: StoredMobileHandoffParams): void {
  try {
    window.sessionStorage.setItem(MOBILE_HANDOFF_STORAGE_KEY, JSON.stringify(params));
  } catch {
    // Ignore storage failures; URL params remain the primary source.
  }
}

function clearStoredMobileHandoffParams(): void {
  try {
    window.sessionStorage.removeItem(MOBILE_HANDOFF_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function getFocusedChatIdentityId(pathname: string): string | null {
  const parts = pathname.split('/').filter(Boolean);
  try {
    if (parts[0] === 'messages' && parts[1] === 'group' && parts.length >= 4) {
      return decodeURIComponent(parts[2] || '').trim() || null;
    }
    if (parts[0] === 'messages' && parts[1] && parts[1] !== 'new' && parts[1] !== 'group' && parts[2]) {
      return decodeURIComponent(parts[1]).trim() || null;
    }
  } catch {
    return null;
  }
  return null;
}

function App() {
  const initializeCallHistory = useCallHistoryStore((state) => state.initialize);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { initialize, isInitialized, initError } = useAuthStore();
  const hasIdentity = useAuthStore(selectHasIdentity);
  const activeCall = useCallStore((s) => s.activeCall);
  const incomingCall = useCallStore((s) => s.incomingCall);
  const setCallMinimized = useCallStore((s) => s.setCallMinimized);
  const restoreCall = useCallStore((s) => s.restoreCall);
  const dismissCallState = useCallStore((s) => s.endCall);
  const didHydrateRef = useRef(false);
  const didLoadVaultRef = useRef(false);
  const multiServerSyncNeededRef = useRef(false);
  const multiServerSyncPromiseRef = useRef<Promise<unknown> | null>(null);
  const didBecomeReadyRef = useRef(false);
  const masterKeyNudgePendingAtSessionStartRef = useRef(shouldShowMasterKeyNudge());
  const prevInCallRef = useRef(false);
  const remoteAudioSinkRef = useRef<HTMLAudioElement>(null);
  const lastMultiServerSyncAtRef = useRef<number>(0);
  const multiServerSyncTimerRef = useRef<number | null>(null);
  const lastMobileHandoffAttemptRef = useRef<string | null>(null);
  const prevMobilePushDeviceSyncKeyRef = useRef<string>('');
  const firstCircleMicPermissionInFlightRef = useRef(false);
  const [multiServerSyncTick, setMultiServerSyncTick] = useState(0);
  const hasAnyDevice = useAuthStore((state) => Object.keys(state.devices).length > 0);
  const mobileDeviceSyncKey = useAuthStore((state) => Object.keys(state.devices).sort().join('|'));
  const isVaultSyncDirty = useVaultStore((state) => state.isSyncDirty);
  useTemporaryMembershipAutoConnect();

  const chatFontSize = useVaultStore(
    (s) => (s.vaultData?.preferences?.['chat.fontSize'] as number | undefined) ?? 15
  );

  useEffect(() => {
    document.documentElement.style.setProperty('--chat-font-size', `${chatFontSize}px`);
  }, [chatFontSize]);

  useEffect(() => {
    if (!isInitialized || !hasAnyDevice) return;
    void startDeviceRegistrationSelfHeal().catch(() => {});
  }, [hasAnyDevice, isInitialized]);

  useEffect(() => {
    if (!isInitialized || !hasAnyDevice || !isVaultSyncDirty) return;

    let cancelled = false;
    let inFlight = false;
    const retryPendingVaultSave = async () => {
      if (cancelled || inFlight || !useVaultStore.getState().isSyncDirty) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

      inFlight = true;
      try {
        const { buildServerSyncContexts, saveVaultToAllServersWithConflictResolution } = await import('./utils/vaultSync');
        const authState = useAuthStore.getState();
        const contexts = buildServerSyncContexts(
          Object.values(authState.identities),
          Object.values(authState.devices)
        );
        if (contexts.length > 0) {
          await saveVaultToAllServersWithConflictResolution(contexts);
        }
      } catch {
        // Keep the persisted pending flag; a future retry can complete the save.
      } finally {
        inFlight = false;
      }
    };

    const onVisibleOrOnline = () => {
      if (document.visibilityState === 'visible') {
        void retryPendingVaultSave();
      }
    };

    void retryPendingVaultSave();
    const timer = window.setInterval(() => {
      void retryPendingVaultSave();
    }, 30_000);
    window.addEventListener('online', onVisibleOrOnline);
    document.addEventListener('visibilitychange', onVisibleOrOnline);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('online', onVisibleOrOnline);
      document.removeEventListener('visibilitychange', onVisibleOrOnline);
    };
  }, [hasAnyDevice, isInitialized, isVaultSyncDirty, mobileDeviceSyncKey]);

  useEffect(() => {
    if (!isInitialized || !isWebViewMode || !hasIdentity || !hasAnyDevice) return;
    if (firstCircleMicPermissionInFlightRef.current) return;
    if (hasWebViewMediaPermissions({ audio: true, video: true })) return;

    firstCircleMicPermissionInFlightRef.current = true;
    void requestWebViewMediaPermissions({ audio: true, video: true })
      .catch(() => {
        // A later call attempt will surface any permission denial in context.
      })
      .finally(() => {
        firstCircleMicPermissionInFlightRef.current = false;
      });
  }, [hasAnyDevice, hasIdentity, isInitialized]);

  useEffect(() => {
    if (!isInitialized || !hasAnyDevice) return;

    let cancelled = false;
    const runHeartbeat = () => {
      if (cancelled) return;
      void sendPresenceHeartbeatAllCircles();
    };
    const runStatusRefresh = () => {
      if (cancelled) return;
      void refreshContactPresenceStatuses();
    };

    runHeartbeat();
    runStatusRefresh();

    const heartbeatTimer = window.setInterval(runHeartbeat, PRESENCE_HEARTBEAT_INTERVAL_MS);
    const refreshTimer = window.setInterval(runStatusRefresh, PRESENCE_STATUS_REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(heartbeatTimer);
      window.clearInterval(refreshTimer);
    };
  }, [hasAnyDevice, isInitialized, mobileDeviceSyncKey]);

  const [callSoundsPref, setCallSoundsPref] = useState<'unset' | 'on' | 'off'>('unset');
  const [mobileHandoffDialog, setMobileHandoffDialog] = useState<MobileHandoffDialogState | null>(null);
  const [mobileHandoffPending, setMobileHandoffPending] = useState<MobileHandoffPendingState | null>(null);
  const [, setMobileHandoffElapsedTick] = useState(0);
  const mobileHandoffCancelRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!activeCall) return;

    let sentinel: any = null;
    let cancelled = false;

    const requestWakeLock = async () => {
      try {
        const wakeLock = (navigator as any)?.wakeLock;
        if (!wakeLock?.request) return;
        if (document.visibilityState !== 'visible') return;
        if (sentinel) return;
        sentinel = await wakeLock.request('screen');
        if (cancelled && sentinel?.release) {
          await sentinel.release();
          sentinel = null;
        }
      } catch {
        // ignore (unsupported or denied)
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    void requestWakeLock();

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      try {
        if (sentinel?.release) {
          void sentinel.release();
        }
      } catch {
        // ignore
      }
      sentinel = null;
    };
  }, [activeCall, activeCall?.callSessionId]);

  useEffect(() => {
    if (!mobileHandoffPending || mobileHandoffPending.stage !== 'exchange') return;

    const timer = window.setInterval(() => {
      setMobileHandoffElapsedTick((tick) => tick + 1);
    }, 500);

    return () => window.clearInterval(timer);
  }, [mobileHandoffPending]);

  useEffect(() => {
    if (!mobileHandoffPending || mobileHandoffPending.stage !== 'exchange') return;
    if (!mobileHandoffPending.handoffId || !mobileHandoffPending.centralBaseUrl) return;

    let stopped = false;
    const timer = window.setInterval(() => {
      if (stopped) return;

      void fetchMobileHandoffStatusViaCentral({
        centralBaseUrl: mobileHandoffPending.centralBaseUrl!,
        handoffId: mobileHandoffPending.handoffId!
      }).then((status) => {
        if (stopped) return;
        setMobileHandoffPending((current) => {
          if (!current || current.stage !== 'exchange') return current;
          if (current.handoffId !== mobileHandoffPending.handoffId) return current;
          if (current.observedCentralStatus === status.status) return current;
          return {
            ...current,
            observedCentralStatus: status.status
          };
        });
      }).catch(() => {
        // Ignore polling failures; the main exchange request/error path remains authoritative.
      });
    }, 2500);

    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [mobileHandoffPending]);

  const beginMobileHandoff = useCallback((params: {
    handoffId: string;
    handoffCode: string;
    centralBaseUrl: string;
  }) => {
    mobileHandoffCancelRef.current?.();

    let cancelled = false;
    mobileHandoffCancelRef.current = () => {
      cancelled = true;
    };

    void (async () => {
      try {
        setMobileHandoffPending({
          stage: 'exchange',
          title: 'Connecting this browser to mobile notifications...',
          detail: describeExchangePending(Date.now()),
          startedAt: Date.now(),
          handoffId: params.handoffId,
          handoffCode: params.handoffCode,
          centralBaseUrl: params.centralBaseUrl
        });

        const exchanged = await exchangeMobileHandoffViaCentral({
          centralBaseUrl: params.centralBaseUrl,
          handoffId: params.handoffId,
          handoffCode: params.handoffCode
        });
        if (cancelled) return;

        setMobileHandoffPending({
          stage: 'register',
          title: 'Reconnect confirmed. Updating your connected servers...',
          detail: 'The mobile app accepted the reconnect request. Updating your connected servers now.',
          startedAt: Date.now()
        });

        const out = await registerMobileDeliveryTokenOnAllKnownServers({
          mobileEndpointId: exchanged.mobileEndpointId,
          deliveryToken: exchanged.deliveryToken,
          deliveryTokenExpiresAt: exchanged.deliveryTokenExpiresAt,
          webOrigin: window.location.origin,
          onProgress: (progress) => {
            if (cancelled) return;
            setMobileHandoffPending({
              stage: 'register',
              title: 'Updating connected servers...',
              detail: formatMobileHandoffProgress(progress),
              startedAt: Date.now()
            });
          }
        });
        if (cancelled) return;

        if (exchanged.deliveryTokenExpiresAt) {
          const okServerKeys = Array.from(new Set(out.ok.map((x) => x.serverKey).filter(Boolean)));
          updateMobileServerTokenValidity(okServerKeys, exchanged.deliveryTokenExpiresAt);
        }

        if (exchanged.refreshGrantId && exchanged.refreshGrantSecret) {
          saveMobileRefreshGrant({
            grantId: exchanged.refreshGrantId,
            grantSecret: exchanged.refreshGrantSecret,
            mobileEndpointId: exchanged.mobileEndpointId,
            centralBaseUrl: params.centralBaseUrl,
            webOrigin: window.location.origin,
            savedAt: Date.now()
          });
        }

        const clean = new URL(window.location.href);
        clean.searchParams.delete('mobile_handoff');
        clean.searchParams.delete('mobile_handoff_code');
        clean.searchParams.delete('mobile_handoff_debug');
        clean.searchParams.delete('mobile_handoff_start');
        window.history.replaceState({}, '', clean.toString());
        clearStoredMobileHandoffParams();

        if (out.failed.length === 0) {
          setMobileHandoffDialog({
            title: 'Notifications reconnected',
            description: `Notifications are now connected to this phone on ${out.ok.length} server(s).`,
            variant: 'success'
          });
          showToast({ message: `Notifications reconnected on ${out.ok.length} server(s).`, variant: 'success' });
        } else {
          setMobileHandoffDialog({
            title: 'Notifications reconnected with some issues',
            description: `Notifications were reconnected on ${out.ok.length} server(s), but ${out.failed.length} still need attention.`,
            details: out.failed.map((item) => `${item.serverKey || item.deviceId}: ${humanizeMobileHandoffError(item.error)}`),
            variant: 'error'
          });
          showToast({ message: `Reconnected on ${out.ok.length} server(s), ${out.failed.length} need attention.`, variant: 'error' });
        }
      } catch (e: any) {
        if (cancelled) return;
        setMobileHandoffDialog({
          title: 'Could not reconnect notifications',
          description: humanizeMobileHandoffError(String(e?.message || e || 'Unknown error')),
          variant: 'error'
        });
        showToast({ message: t('mobileNotifications.errors.reconnect'), variant: 'error' });
      } finally {
        if (!cancelled) {
          setMobileHandoffPending(null);
        }
      }
    })();
  }, [t]);

  useEffect(() => {
    if (!isInitialized || !hasIdentity || !hasAnyDevice) return;

    const searchParams = new URLSearchParams(location.search);
    const bridgeHandoff = consumeMobileHandoffFromBridge();
    const handoffIdFromUrl = (searchParams.get('mobile_handoff') || '').trim();
    const handoffCodeFromUrl = (searchParams.get('mobile_handoff_code') || '').trim();
    const manualDebugModeFromUrl = (searchParams.get('mobile_handoff_debug') || '').trim() === '1';
    const manualDebugStartFromUrl = (searchParams.get('mobile_handoff_start') || '').trim() === '1';
    const handoffIdFromBridge = String(bridgeHandoff?.handoffId || '').trim();
    const handoffCodeFromBridge = String(bridgeHandoff?.handoffCode || '').trim();
    const hasBridgeHandoff = !!handoffIdFromBridge && !!handoffCodeFromBridge;
    const effectiveHandoffId = hasBridgeHandoff ? handoffIdFromBridge : handoffIdFromUrl;
    const effectiveHandoffCode = hasBridgeHandoff ? handoffCodeFromBridge : handoffCodeFromUrl;

    if (effectiveHandoffId && effectiveHandoffCode) {
      saveStoredMobileHandoffParams({
        handoffId: effectiveHandoffId,
        handoffCode: effectiveHandoffCode,
        manualDebugMode: manualDebugModeFromUrl,
        manualDebugStart: manualDebugStartFromUrl
      });
    }

    const storedParams = loadStoredMobileHandoffParams();
    const handoffId = effectiveHandoffId || storedParams?.handoffId || '';
    const handoffCode = effectiveHandoffCode || storedParams?.handoffCode || '';
    const manualDebugMode = effectiveHandoffId
      ? manualDebugModeFromUrl
      : (storedParams?.manualDebugMode || false);
    const manualDebugStart = effectiveHandoffId
      ? manualDebugStartFromUrl
      : (storedParams?.manualDebugStart || false);
    const centralBaseUrl = String(import.meta.env.VITE_MOBILE_PUSH_CENTRAL_URL || '').trim().replace(/\/+$/, '');
    if (!handoffId || !handoffCode) {
      lastMobileHandoffAttemptRef.current = null;
      mobileHandoffCancelRef.current?.();
      mobileHandoffCancelRef.current = null;
      return;
    }

    const handoffAttemptKey = `${handoffId}:${handoffCode}`;
    if (manualDebugMode && !manualDebugStart) {
      setMobileHandoffPending({
        stage: 'manual',
        title: 'Ready to reconnect notifications',
        detail: 'Press Continue to start the reconnect in this browser tab.',
        startedAt: Date.now(),
        handoffId,
        handoffCode,
        centralBaseUrl
      });
      return;
    }
    if (lastMobileHandoffAttemptRef.current === handoffAttemptKey) {
      return;
    }
    lastMobileHandoffAttemptRef.current = handoffAttemptKey;

    if (!centralBaseUrl) {
      showToast({ message: t('mobileNotifications.errors.reconnect'), variant: 'error' });
      return;
    }
    beginMobileHandoff({ handoffId, handoffCode, centralBaseUrl });
    return () => {
      mobileHandoffCancelRef.current?.();
      mobileHandoffCancelRef.current = null;
    };
  }, [beginMobileHandoff, hasAnyDevice, hasIdentity, isInitialized, location.search, t]);


  useEffect(() => {
    if (!isInitialized || !hasIdentity || !hasAnyDevice) return;

    const stored = loadMobileRefreshGrant();
    if (!stored) return;

    const configuredCentral = String(import.meta.env.VITE_MOBILE_PUSH_CENTRAL_URL || '').trim().replace(/\/+$/, '');
    if (!configuredCentral || configuredCentral !== stored.centralBaseUrl.replace(/\/+$/, '')) {
      return;
    }

    const knownServerKeys = getKnownServerKeys();

    // If a new device was just added, always re-register even if the token is still valid.
    // The new device hasn't had the delivery token registered on the server yet.
    const prevSyncKey = prevMobilePushDeviceSyncKeyRef.current;
    prevMobilePushDeviceSyncKeyRef.current = mobileDeviceSyncKey;
    const prevCount = prevSyncKey ? prevSyncKey.split('|').filter(Boolean).length : 0;
    const currentCount = mobileDeviceSyncKey.split('|').filter(Boolean).length;
    const deviceCountIncreased = currentCount > prevCount;

    if (!deviceCountIncreased && !shouldRefreshDeliveryTokenForKnownServers({
      knownServerKeys,
      thresholdMs: 30 * 24 * 60 * 60 * 1000
    })) {
      return;
    }

    let cancelled = false;

    const syncWithRefreshGrant = async () => {
      try {
        const redeemed = await redeemMobileRefreshGrantViaCentral({
          centralBaseUrl: configuredCentral,
          grantId: stored.grantId,
          grantSecret: stored.grantSecret
        });
        if (cancelled) return;

        const out = await registerMobileDeliveryTokenOnAllKnownServers({
          mobileEndpointId: redeemed.mobileEndpointId,
          deliveryToken: redeemed.deliveryToken,
          deliveryTokenExpiresAt: redeemed.deliveryTokenExpiresAt,
          webOrigin: window.location.origin
        });
        if (cancelled) return;

        if (redeemed.deliveryTokenExpiresAt) {
          const okServerKeys = Array.from(new Set(out.ok.map((x) => x.serverKey).filter(Boolean)));
          updateMobileServerTokenValidity(okServerKeys, redeemed.deliveryTokenExpiresAt);
        }

      } catch (e: any) {
        const msg = String(e?.message || e);
        if (/revoked|not found|expired/i.test(msg)) {
          clearMobileRefreshGrant();
        }
      }
    };

    void syncWithRefreshGrant();

    return () => {
      cancelled = true;
    };
  }, [hasAnyDevice, hasIdentity, isInitialized, mobileDeviceSyncKey]);

  const [callSoundsUnlocked, setCallSoundsUnlocked] = useState(false);
  const isCallRoute = location.pathname === '/call' || location.pathname.startsWith('/call/');

  // Initialize call handling (WebSocket listeners)
  useCallListeners();
  useDirectFileTransferListeners();
  useEffect(() => {
    void initializeCallHistory();
  }, [initializeCallHistory]);

  useEffect(() => {
    initializeMessaging();
    initializeCallHistorySync();
    initializeSystemEvents();
    initializeTemporaryTrustedAccessEvents();
    initializeMessageArchiveWriter();
  }, []);

  useEffect(() => {
    const handleServerAccessRemoved = (event: Event) => {
      const identityId = String((event as CustomEvent<{ identityId?: string }>).detail?.identityId || '').trim();
      if (!identityId || !useAuthStore.getState().identities[identityId as keyof ReturnType<typeof useAuthStore.getState>['identities']]) return;

      void (async () => {
        const removeLocalCopy = await requestConfirmation({
          title: t('servers.serverDataDeletedTitle'),
          description: t('servers.serverDataDeletedDescription'),
          confirmText: t('servers.serverDataDeletedRemoveLocal'),
          cancelText: t('servers.serverDataDeletedKeepLocal'),
          variant: 'danger'
        });
        if (!removeLocalCopy) {
          showToast({ message: t('servers.serverDataDeletedKeptLocally'), variant: 'info', durationMs: 9000 });
          return;
        }

        try {
          const { wasLastCircle } = await deleteRemovedCircleLocalCopy(identityId);
          if (wasLastCircle) {
            await clearLocalBrowserProfile();
            window.location.replace('/');
            return;
          }
          showToast({ message: t('servers.serverDataDeletedRemovedLocally'), variant: 'success' });
          navigate('/servers');
        } catch (error) {
          showToast({
            message: t('servers.errors.clearLocalProfileFailed', {
              message: error instanceof Error ? error.message : t('common.unknownError')
            }),
            variant: 'error'
          });
        }
      })();
    };

    const handleRevokedDeviceLocalDeletion = (event: Event) => {
      const detail = (event as CustomEvent<{
        identityId?: string;
        localDeletionAuthorization?: DeviceLocalCircleDataDeletionAuthorization;
      }>).detail;
      const identityId = String(detail?.identityId || '').trim();
      const authorization = detail?.localDeletionAuthorization;
      const auth = useAuthStore.getState();
      const identity = auth.identities[identityId as keyof typeof auth.identities];
      const targetDeviceId = authorization?.payload?.targetDeviceId;
      const targetDevice = targetDeviceId ? auth.devices[targetDeviceId as keyof typeof auth.devices] : undefined;
      if (!identityId || !identity || !targetDevice || targetDevice.identityId !== identityId || !authorization) return;

      void (async () => {
        const isAuthorized = await verifyLocalCircleDataDeletionAuthorization({
          authorization,
          identityId: identity.identityId,
          targetDeviceId: targetDevice.deviceId,
          identityPublicKey: identity.publicKey
        });
        if (!isAuthorized) {
          console.warn('Ignored invalid local Circle data deletion authorization after device revoke');
          return;
        }
        try {
          const { wasLastCircle } = await deleteRemovedCircleLocalCopy(identityId);
          if (wasLastCircle) {
            await clearLocalBrowserProfile();
            window.location.replace('/');
            return;
          }
          showToast({ message: t('devices.localCircleDataDeletedAfterRevoke'), variant: 'info', durationMs: 9000 });
          navigate('/servers');
        } catch (error) {
          showToast({
            message: t('servers.errors.clearLocalProfileFailed', {
              message: error instanceof Error ? error.message : t('common.unknownError')
            }),
            variant: 'error'
          });
        }
      })();
    };

    window.addEventListener('circlus:server-access-removed', handleServerAccessRemoved);
    window.addEventListener('circlus:delete-revoked-device-local-data', handleRevokedDeviceLocalDeletion);
    return () => {
      window.removeEventListener('circlus:server-access-removed', handleServerAccessRemoved);
      window.removeEventListener('circlus:delete-revoked-device-local-data', handleRevokedDeviceLocalDeletion);
    };
  }, [navigate, t]);

  const minMultiServerSyncIntervalMs = (() => {
    const raw = Number(import.meta.env.VITE_MULTI_SERVER_SYNC_MIN_INTERVAL_MS || 3 * 60 * 1000);
    if (!Number.isFinite(raw) || raw < 0) return 3 * 60 * 1000;
    return Math.floor(raw);
  })();

  const scheduleMultiServerSyncRetry = (waitMs: number) => {
    if (waitMs <= 0) {
      setMultiServerSyncTick((v) => v + 1);
      return;
    }

    if (multiServerSyncTimerRef.current !== null) return;

    multiServerSyncTimerRef.current = window.setTimeout(() => {
      multiServerSyncTimerRef.current = null;
      setMultiServerSyncTick((v) => v + 1);
    }, waitMs);
  };

  useEffect(() => {
    // Best-effort sync messages from ALL servers sequentially.
    // - Run once on "app ready".
    // - If it was skipped/interrupted (e.g. user was in a call), run again when safe.
    // - Never start while in a call, to avoid disrupting call signaling.

    if (!hasIdentity || !hasAnyDevice) return;
    if (!isInitialized) return;

    let hasPendingCallAction = false;
    try {
      hasPendingCallAction = !!sessionStorage.getItem('videocall.pendingCallAction');
    } catch {
      hasPendingCallAction = false;
    }

    const inCall = !!activeCall || !!incomingCall || isCallRoute || hasPendingCallAction || wsClient.isExternalMode();

    // Mark initial run as needed once auth+init are ready.
    if (!didBecomeReadyRef.current) {
      didBecomeReadyRef.current = true;
      multiServerSyncNeededRef.current = true;
    }

    const focusedChatIdentityId = getFocusedChatIdentityId(location.pathname);
    if (focusedChatIdentityId) {
      // A visible thread performs its own focused sync. Keep the broad
      // background sweep pending so it can resume after the user leaves.
      multiServerSyncNeededRef.current = true;
      return;
    }

    if (inCall) {
      // If we're in a call, postpone. We keep "needed" as-is.
      return;
    }

    if (!multiServerSyncNeededRef.current) return;
    if (multiServerSyncPromiseRef.current) return;

    const now = Date.now();
    const lastAt = lastMultiServerSyncAtRef.current || 0;
    const elapsed = now - lastAt;
    if (minMultiServerSyncIntervalMs > 0 && elapsed < minMultiServerSyncIntervalMs) {
      // Throttle: run at most once per X minutes.
      scheduleMultiServerSyncRetry(minMultiServerSyncIntervalMs - elapsed);
      return;
    }

    const shouldContinue = () => {
      const callState = useCallStore.getState();
      if (callState.activeCall || callState.incomingCall) return false;
      if (window.location.pathname === '/call' || window.location.pathname.startsWith('/call/')) return false;
      if (getFocusedChatIdentityId(window.location.pathname)) return false;
      if (wsClient.isExternalMode()) return false;
      try {
        if (sessionStorage.getItem('videocall.pendingCallAction')) return false;
      } catch {
        // ignore
      }
      return true;
    };

    multiServerSyncPromiseRef.current = syncAllServersSequentiallyWithResult({
      shouldContinue,
      shouldRestoreOriginalDevice: () => !getFocusedChatIdentityId(window.location.pathname)
    })
      .then((result) => {
        lastMultiServerSyncAtRef.current = Date.now();
        // If interrupted (e.g. a call started), keep it marked as needed
        // so we retry once things are calm again.
        multiServerSyncNeededRef.current = !result.completed;

        // If still needed but throttled, schedule a retry.
        if (multiServerSyncNeededRef.current && minMultiServerSyncIntervalMs > 0) {
          scheduleMultiServerSyncRetry(minMultiServerSyncIntervalMs);
        }
      })
      .finally(() => {
        multiServerSyncPromiseRef.current = null;
      });
  }, [hasAnyDevice, hasIdentity, isInitialized, activeCall, incomingCall, isCallRoute, location.pathname, multiServerSyncTick, minMultiServerSyncIntervalMs]);

  useEffect(() => {
    // If we were in a call (or on /call), trigger a re-sync when it ends.
    // This satisfies: "run again if we skipped or didn't finish".
    if (!hasIdentity || !hasAnyDevice || !isInitialized) return;

    const inCall = !!activeCall || !!incomingCall || isCallRoute;
    const wasInCall = prevInCallRef.current;
    prevInCallRef.current = inCall;

    if (wasInCall && !inCall) {
      // Mark as needed; the runner effect above will start it when safe.
      multiServerSyncNeededRef.current = true;

      // If we're throttled, ensure we still run later.
      const now = Date.now();
      const lastAt = lastMultiServerSyncAtRef.current || 0;
      const elapsed = now - lastAt;
      if (minMultiServerSyncIntervalMs > 0 && elapsed < minMultiServerSyncIntervalMs) {
        scheduleMultiServerSyncRetry(minMultiServerSyncIntervalMs - elapsed);
      } else {
        scheduleMultiServerSyncRetry(0);
      }
    }
  }, [hasAnyDevice, hasIdentity, isInitialized, activeCall, incomingCall, isCallRoute, minMultiServerSyncIntervalMs]);

  useEffect(() => {
    return () => {
      if (multiServerSyncTimerRef.current !== null) {
        window.clearTimeout(multiServerSyncTimerRef.current);
        multiServerSyncTimerRef.current = null;
      }
    };
  }, [initialize]);

  useEffect(() => {
    if (!hasIdentity) return;

    try {
      const v = localStorage.getItem('videocall.callSoundsEnabled');
      if (v === '1') setCallSoundsPref('on');
      else if (v === '0') setCallSoundsPref('off');
      else setCallSoundsPref('unset');
    } catch {
      setCallSoundsPref('unset');
    }

    // Per-page-load: after reload audio is locked again by browser policies,
    // so we intentionally reset this flag and ask for a new gesture.
    setCallSoundsUnlocked(false);

    const onUnlocked = () => {
      setCallSoundsUnlocked(true);
    };

    window.addEventListener('videocall:soundsUnlocked', onUnlocked);
    return () => {
      window.removeEventListener('videocall:soundsUnlocked', onUnlocked);
    };
  }, [hasIdentity]);

  useEffect(() => {
    // In installed PWA mode we don't want to show a persistent banner on every launch.
    // Instead, we opportunistically unlock audio on the first user interaction.
    // This still respects browser autoplay policies (requires a gesture) but removes the nag.
    if (!hasIdentity) return;
    if (callSoundsPref !== 'on') return;
    if (callSoundsUnlocked) return;

    let removed = false;
    const unlockOnce = () => {
      if (removed) return;
      removed = true;
      window.removeEventListener('pointerdown', unlockOnce);
      window.removeEventListener('keydown', unlockOnce);
      window.removeEventListener('touchstart', unlockOnce);

      void tryUnlockCallSounds().then((ok) => {
        if (ok) setCallSoundsUnlocked(true);
      });
    };

    window.addEventListener('pointerdown', unlockOnce, { passive: true });
    window.addEventListener('keydown', unlockOnce, { passive: true });
    window.addEventListener('touchstart', unlockOnce, { passive: true });

    return () => {
      if (removed) return;
      window.removeEventListener('pointerdown', unlockOnce);
      window.removeEventListener('keydown', unlockOnce);
      window.removeEventListener('touchstart', unlockOnce);
    };
  }, [hasIdentity, callSoundsPref, callSoundsUnlocked]);

  useEffect(() => {
    if (!hasIdentity || !hasAnyDevice) return;
    if (!isPushSupported()) return;

    const attemptAtKey = 'videocall.pushSetupAttemptAt.global';
    const attemptBuildKey = 'videocall.pushSetupAttemptBuild.global';
    const now = Date.now();
    const minRetryMs = 10 * 60 * 1000;

    try {
      // If the user denied notifications, don't keep re-prompting.
      if ('Notification' in window && Notification.permission === 'denied') return;

      // Avoid spamming the prompt, but allow retry if the previous attempt did not change permission.
      // Also allow re-trying after a deploy/build change (useful when push was introduced recently).
      const lastAttemptAt = Number(localStorage.getItem(attemptAtKey) || '0');
      const lastAttemptBuild = localStorage.getItem(attemptBuildKey) || '';
      const currentBuild = (typeof __BUILD_ID__ !== 'undefined' ? String(__BUILD_ID__) : '');
      const buildChanged = !!currentBuild && lastAttemptBuild && lastAttemptBuild !== currentBuild;
      const canRetry = !Number.isFinite(lastAttemptAt) || now - lastAttemptAt >= minRetryMs || buildChanged;
      if (!canRetry) return;
    } catch {
      // ignore
    }

    let removed = false;
    const requestOnce = () => {
      if (removed) return;
      removed = true;
      window.removeEventListener('pointerdown', requestOnce);
      window.removeEventListener('keydown', requestOnce);
      window.removeEventListener('touchstart', requestOnce);

      (async () => {
        // Record attempt before prompting to avoid repeated prompts on the same gesture burst.
        try {
          localStorage.setItem(attemptAtKey, String(Date.now()));
          const currentBuild = (typeof __BUILD_ID__ !== 'undefined' ? String(__BUILD_ID__) : '');
          if (currentBuild) {
            localStorage.setItem(attemptBuildKey, currentBuild);
          }
        } catch {
          // ignore
        }

        try {
          const result = await enablePushNotificationsOnAllKnownServers();
          if (!result.ok) {
            showToast({
              message: result.reason || 'Push notifications are not enabled',
              variant: 'warning',
              durationMs: 7000
            });
          }
        } catch (e) {
          console.error('Push setup failed:', e);
          showToast({
            message: e instanceof Error ? e.message : 'Push setup failed',
            variant: 'error',
            durationMs: 7000
          });
        }
      })();
    };

    window.addEventListener('pointerdown', requestOnce, { passive: true });
    window.addEventListener('keydown', requestOnce, { passive: true });
    window.addEventListener('touchstart', requestOnce, { passive: true });

    return () => {
      if (removed) return;
      window.removeEventListener('pointerdown', requestOnce);
      window.removeEventListener('keydown', requestOnce);
      window.removeEventListener('touchstart', requestOnce);
    };
  }, [hasAnyDevice, hasIdentity]);

  // When a new device is added (mobileDeviceSyncKey changes), reset the push setup cooldown
  // for the current device so that push is re-registered for ALL devices on the next opportunity.
  // This handles the case where a second identity is added: the new device becomes current,
  // but the old device may not have push registered and can no longer receive calls via WebSocket.
  const prevMobileDeviceSyncKeyRef = useRef<string>('');
  useEffect(() => {
    if (!hasIdentity || !hasAnyDevice) return;
    if (!isPushSupported()) return;

    const currentKey = mobileDeviceSyncKey;
    const prevKey = prevMobileDeviceSyncKeyRef.current;
    prevMobileDeviceSyncKeyRef.current = currentKey;

    // Only act when devices were added (key grew), not on initial load or removal.
    if (!prevKey || currentKey === prevKey) return;
    const prevCount = prevKey ? prevKey.split('|').filter(Boolean).length : 0;
    const currentCount = currentKey.split('|').filter(Boolean).length;
    if (currentCount <= prevCount) return;

    try {
      localStorage.removeItem('videocall.pushSetupAttemptAt.global');
      localStorage.removeItem('videocall.pushSetupAttemptBuild.global');
    } catch {
      // ignore
    }

    // Native WebView push does not use the browser Notification permission.
    // In browsers, avoid prompting here and only re-register when permission already exists.
    if (isWebViewMode || ('Notification' in window && Notification.permission === 'granted')) {
      void enablePushNotificationsOnAllKnownServers().catch(() => {});
    }
  }, [hasAnyDevice, hasIdentity, mobileDeviceSyncKey]);

  useEffect(() => {
    // Initialize auth store (load keys from IndexedDB)
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (!activeCall) {
      setCallMinimized(false);
      return;
    }
    if (isWebViewMode && activeCall.nativeRuntimeMode === 'video-native') {
      // Native call UI is a separate Android Activity, so route-based minimization
      // is not a reliable signal here. Preserve explicit minimize state only.
      return;
    }
    if (isCallRoute) {
      restoreCall();
      return;
    }
    setCallMinimized(true);
  }, [activeCall, activeCall?.callSessionId, isCallRoute, restoreCall, setCallMinimized]);

  useEffect(() => {
    const handleNativeCallMinimized = () => {
      const currentCall = useCallStore.getState().activeCall;
      if (!currentCall || currentCall.nativeRuntimeMode !== 'video-native') return;
      setCallMinimized(true);
    };

    window.addEventListener('videocall:nativeCallMinimized', handleNativeCallMinimized);
    return () => {
      window.removeEventListener('videocall:nativeCallMinimized', handleNativeCallMinimized);
    };
  }, [setCallMinimized]);

  useEffect(() => {
    const syncRemoteAudio = () => {
      const audioEl = remoteAudioSinkRef.current;
      if (!audioEl) return;
      if (isWebViewMode && activeCall) {
        if (audioEl.srcObject) {
          audioEl.pause();
          audioEl.srcObject = null;
        }
        return;
      }

      const remoteStream = webrtcManager.getRemoteStream();
      if (!remoteStream) {
        if (audioEl.srcObject) {
          audioEl.pause();
          audioEl.srcObject = null;
        }
        return;
      }

      if (audioEl.srcObject !== remoteStream) {
        audioEl.srcObject = remoteStream;
      }

      if (activeCall) {
        audioEl.play().catch(() => {
          // autoplay can be temporarily blocked by browser policy
        });
      }
    };

    const scheduleRemoteAudioReplay = () => {
      window.setTimeout(() => {
        syncRemoteAudio();
      }, 150);
    };

    const handleVisibilityAudioResume = () => {
      if (document.visibilityState !== 'visible') return;
      scheduleRemoteAudioReplay();
    };

    syncRemoteAudio();
    const interval = window.setInterval(syncRemoteAudio, 500);
    document.addEventListener('visibilitychange', handleVisibilityAudioResume);
    window.addEventListener('pageshow', scheduleRemoteAudioReplay);
    window.addEventListener('focus', scheduleRemoteAudioReplay);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityAudioResume);
      window.removeEventListener('pageshow', scheduleRemoteAudioReplay);
      window.removeEventListener('focus', scheduleRemoteAudioReplay);
    };
  }, [activeCall, activeCall?.callSessionId, activeCall?.status]);

  useEffect(() => {
    // Best-effort hydration of server metadata for existing identities.
    // This keeps UI labels stable after reloads in typical single-server deployments.
    if (!isInitialized || didHydrateRef.current) return;
    didHydrateRef.current = true;

    (async () => {
      const { updateIdentityMeta } = await import('./crypto/storage');
      const state = useAuthStore.getState();
      const identities = Object.values(state.identities);
      if (identities.length === 0) return;

      // 1) Ensure serverId is always derived from serverUrl origin (never from server-provided ids)
      for (const identity of identities) {
        const normalizedUrl = normalizeServerUrl(identity.serverUrl) || identity.serverUrl;
        const derivedServerId = serverKeyFromServerUrl(normalizedUrl) || identity.serverId;

        if (normalizedUrl !== identity.serverUrl || derivedServerId !== identity.serverId) {
          state.updateIdentity(identity.identityId, {
            serverId: derivedServerId,
            serverUrl: normalizedUrl
          });

          await updateIdentityMeta(identity.identityId, {
            serverId: derivedServerId,
            serverUrl: normalizedUrl
          });
        }
      }

      // Backfill role for older local records. Existing local role is the source of truth for offline UI.
      for (const identity of identities) {
        if (!identity.serverUrl || identity.role) continue;
        try {
          const resp = await authAPI.getEncryptedIdentityKey(identity.publicKey, identity.serverUrl);
          if (resp.status === 'ok' && resp.result?.role) {
            state.updateIdentity(identity.identityId, { role: resp.result.role });
            await updateIdentityMeta(identity.identityId, {
              role: resp.result.role
            });
          }
        } catch {
          // ignore
        }
      }

      // Backfill vpsId for existing identities that don't have it yet
      const identitiesMissingVpsId = Object.values(useAuthStore.getState().identities).filter(
        (id) => id.serverUrl && !id.vpsId
      );
      if (identitiesMissingVpsId.length > 0) {
        const { serverAPI } = await import('./api/server');
        const { signRequestWithKey } = await import('./crypto/signing');
        const currentState = useAuthStore.getState();

        for (const identity of identitiesMissingVpsId) {
          const device = Object.values(currentState.devices).find((d) => d.identityId === identity.identityId);
          if (!device?.signingKey) continue;
          try {
            const signed = await signRequestWithKey('server:vps-id', {}, device.deviceId, device.signingKey);
            const resp = await serverAPI.getVpsId(signed, identity.serverUrl);
            if (resp.status === 'ok' && resp.result?.vpsId) {
              currentState.updateIdentity(identity.identityId, { vpsId: resp.result.vpsId });
              await updateIdentityMeta(identity.identityId, { vpsId: resp.result.vpsId });
            }
          } catch {
            // ignore — will retry on next init
          }
        }
      }
    })();
  }, [isInitialized]);

  // When the app is restored to a deep screen (Back-only exit) with no browser history,
  // build synthetic history so the Back button works correctly.
  useEffect(() => {
    if (!isWebViewMode) return;
    const parentTab = getDeepRouteParentTab(location.pathname);
    if (parentTab && !canNavigateBackInApp()) {
      navigate(parentTab, { replace: true });
      navigate(location.pathname + location.search + location.hash);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isWebViewMode) return;

    window.__circlusHandleNativeBack = () => {
      if (window.history.state?.circlusFullscreenAttachmentToken) {
        window.history.back();
        return 'handled';
      }

      const parentTab = getDeepRouteParentTab(window.location.pathname);
      if (parentTab) {
        if (canNavigateBackInApp()) {
          navigate(-1);
        } else {
          navigate(parentTab, { replace: true });
        }
        return 'handled';
      }

      return 'exit';
    };

    return () => {
      delete window.__circlusHandleNativeBack;
    };
  }, [navigate]);

  useEffect(() => {
    // Load vault after auth store is initialized and we have keys.
    // Without this, contacts only exist in memory and disappear after refresh.
    if (!isInitialized || didLoadVaultRef.current) return;
    if (!hasAnyDevice) return;
    didLoadVaultRef.current = true;

    (async () => {
      const vaultStore = useVaultStore.getState();
      vaultStore.setLoading(true);
      try {
        const { buildServerSyncContexts, loadLocalVaultIntoStore, refreshVaultFromAllServers, refreshCircleOwnerInfo } = await import('./utils/vaultSync');

        // 1) Load local vault first (fast; works offline)
        await loadLocalVaultIntoStore();

        // 2) Refresh from every server where we have an identity
        const authState = useAuthStore.getState();
        const identities = Object.values(authState.identities);
        const devices = Object.values(authState.devices);

        const serverContexts = buildServerSyncContexts(identities, devices).filter(
          (context) => wsClient.getServerConnectionSnapshot(context.serverUrl).issueCode !== 'auth_failed'
        );

        if (serverContexts.length > 0) {
          let { failedContexts } = await refreshVaultFromAllServers(serverContexts);

          // Retry only the servers that were temporarily unreachable at startup.
          const MAX_RETRIES = 3;
          const RETRY_DELAY_MS = 15_000;
          for (let attempt = 0; attempt < MAX_RETRIES && failedContexts.length > 0; attempt++) {
            await new Promise<void>((resolve) => { window.setTimeout(resolve, RETRY_DELAY_MS); });
            ({ failedContexts } = await refreshVaultFromAllServers(failedContexts));
          }

          // Background: populate owner identity info for circles that don't have it yet.
          void refreshCircleOwnerInfo(serverContexts);
        }

        // Ensure vault is initialized even if server has none.
        const currentVaultState = useVaultStore.getState();
        if (!currentVaultState.vaultData) {
          currentVaultState.loadVault(
            {
              version: 1,
              contacts: {},
              groups: {},
              localData: { contactStatuses: {} },
              meta: { updatedAt: new Date().toISOString() }
            },
            {}
          );
        }
      } catch {
        // Non-fatal: vault will load on next sync
      } finally {
        useVaultStore.getState().setLoading(false);
      }
    })();
  }, [hasAnyDevice, isInitialized]);

  useEffect(() => {
    if (!isInitialized) return;
    if (!masterKeyNudgePendingAtSessionStartRef.current) return;

    const authState = useAuthStore.getState();
    if (authState.masterKey) return;

    const hasTrustedIdentity = Object.values(authState.identities).some(
      (identity) => identity.role !== 'temporary'
    );
    if (!hasTrustedIdentity) return;

    const path = location.pathname;
    const isMainAppPath = (
      path === '/'
      || path === '/messages'
      || path === '/contacts'
      || path === '/calls'
      || path === '/servers'
      || path === '/settings'
    );

    if (!isMainAppPath) return;

    masterKeyNudgePendingAtSessionStartRef.current = false;
    consumeMasterKeyNudge();
    navigate('/master-key', {
      replace: true,
      state: {
        promptMode: 'nudge',
        returnTo: `${location.pathname}${location.search}`
      }
    });
  }, [isInitialized, location.pathname, location.search, navigate]);

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (initError === 'ed25519_not_supported') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{t('common.browserNotSupportedTitle')}</h1>
          <p className="text-gray-600">{t('common.browserNotSupportedMessage')}</p>
        </div>
      </div>
    );
  }

  if (initError === 'android_keys_missing') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{t('common.androidKeysMissingTitle')}</h1>
          <p className="text-gray-600">{t('common.androidKeysMissingMessage')}</p>
        </div>
      </div>
    );
  }

  const shouldShowCallSoundsPrompt =
    hasIdentity &&
    !activeCall &&
    !incomingCall &&
    !isCallRoute &&
    !isWebViewMode &&
    // Only ask for the preference once. After that, we unlock on the first natural gesture.
    callSoundsPref === 'unset';

  const callSoundsPrompt = shouldShowCallSoundsPrompt ? (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
        <div className="text-sm text-gray-700">
          {t('callSounds.prompt')}
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          <button
            className="btn btn-primary w-full sm:w-auto"
            onClick={() => {
              if (callSoundsPref === 'unset') {
                try {
                  localStorage.setItem('videocall.callSoundsEnabled', '1');
                } catch {
                  // ignore
                }
                setCallSoundsPref('on');
              }

              // Must happen on user gesture.
              void tryUnlockCallSounds().then((ok) => {
                if (!ok) return;
                setCallSoundsUnlocked(true);
              });
            }}
          >
            {callSoundsPref === 'unset' ? t('callSounds.enable') : t('callSounds.activate')}
          </button>
          <button
            className="btn btn-secondary w-full sm:w-auto"
            onClick={() => {
              if (callSoundsPref === 'unset') {
                try {
                  localStorage.setItem('videocall.callSoundsEnabled', '0');
                } catch {
                  // ignore
                }
                setCallSoundsPref('off');
              } else {
                // Don't change preference; just hide the prompt for this page load.
                setCallSoundsUnlocked(true);
              }
            }}
          >
            {callSoundsPref === 'unset' ? t('common.notNow') : t('callSounds.hide')}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <audio ref={remoteAudioSinkRef} autoPlay playsInline className="hidden" />
      {callSoundsPrompt}
      <IncomingCallPassiveBanner />
      {isWebViewMode && activeCall?.preparationError ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t('call.errors.startFailedTitle')}
            className="w-full max-w-md overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
          >
            <div className="border-b border-gray-100 px-5 py-4">
              <div className="text-base font-semibold text-gray-900">{t('call.errors.startFailedTitle')}</div>
              <div className="mt-2 text-sm leading-6 text-gray-600">{activeCall.preparationError}</div>
            </div>
            <div className="flex justify-end px-5 py-4">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  dismissCallState();
                  navigate(consumeCallReturnPath('/messages'));
                }}
              >
                {t('common.ok')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {mobileHandoffPending ? (
        <div className="fixed inset-x-0 top-0 z-[90] flex justify-center px-4 pt-4 pointer-events-none">
          <div className="pointer-events-auto flex items-center gap-3 rounded-full border border-primary-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-600 border-r-transparent" />
            <div>
              <div className="text-sm font-medium text-gray-900">{mobileHandoffPending.title}</div>
              {mobileHandoffPending.detail ? (
                <div className="text-xs text-gray-600">
                  {mobileHandoffPending.stage === 'exchange'
                    ? describeExchangePending(
                        mobileHandoffPending.startedAt,
                        mobileHandoffPending.observedCentralStatus
                      )
                    : mobileHandoffPending.detail}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      {mobileHandoffPending?.stage === 'manual' ? (
        <div className="fixed inset-0 z-[95] bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl">
            <div className="text-lg font-semibold text-gray-900">Reconnect notifications</div>
            <div className="mt-2 text-sm text-gray-600">
              Press Continue to reconnect this browser with the mobile app and refresh notification delivery.
            </div>
            <div className="mt-4 flex justify-end">
              {isWebViewMode ? (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    if (!mobileHandoffPending?.handoffId || !mobileHandoffPending?.handoffCode) return;
                    const centralBaseUrl = String(import.meta.env.VITE_MOBILE_PUSH_CENTRAL_URL || '').trim().replace(/\/+$/, '');
                    if (!centralBaseUrl) {
                      showToast({ message: t('mobileNotifications.errors.reconnect'), variant: 'error' });
                      return;
                    }
                    beginMobileHandoff({
                      handoffId: mobileHandoffPending.handoffId,
                      handoffCode: mobileHandoffPending.handoffCode,
                      centralBaseUrl
                    });
                  }}
                >
                  Continue
                </button>
              ) : (
                <a
                  className="btn btn-primary"
                  href={
                    mobileHandoffPending?.handoffId && mobileHandoffPending?.handoffCode
                      ? `${location.pathname}?mobile_handoff=${encodeURIComponent(mobileHandoffPending.handoffId)}&mobile_handoff_code=${encodeURIComponent(mobileHandoffPending.handoffCode)}&mobile_handoff_debug=1&mobile_handoff_start=1`
                      : undefined
                  }
                >
                  Continue
                </a>
              )}
            </div>
          </div>
        </div>
      ) : null}
      <ConfirmDialog
        open={!!mobileHandoffDialog}
        title={mobileHandoffDialog?.title || ''}
        description={mobileHandoffDialog?.description}
        confirmText="OK"
        cancelText="Close"
        variant={mobileHandoffDialog?.variant === 'error' ? 'danger' : 'primary'}
        onConfirm={() => setMobileHandoffDialog(null)}
        onCancel={() => setMobileHandoffDialog(null)}
      >
        {mobileHandoffDialog?.details?.length ? (
          <div className="space-y-2">
            {mobileHandoffDialog.details.map((item) => (
              <div key={item} className="text-sm text-gray-700 break-words">
                {item}
              </div>
            ))}
          </div>
        ) : null}
      </ConfirmDialog>
      <IncomingCallModal />
      <DirectFileTransferOverlay />
      <Suspense fallback={<RouteLoading />}>
        <Routes>
        {/* Public routes */}
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />

        {/* Add server page - accessible to everyone */}
        <Route path="/start" element={<LazyFirstRunPage />} />
        <Route path="/resume-registration/:identityId?" element={<LazyPendingRegistrationPage />} />
        <Route path="/start/create-circle" element={<LazySelfHostedCreateCirclePage />} />
        <Route path="/restore-from-backup" element={<LazyRestoreFromBackupPage />} />
        <Route path="/recover-circles-from-backup" element={<LazyRecoverCirclesFromBackupPage />} />
        <Route path="/add-server" element={<LazyAddServerPage />} />

        {/* Master key setup / transfer */}
        <Route path="/master-key" element={<LazyMasterKeySetupPage />} />
        <Route path="/master-key/request" element={<LazyMasterKeyRequestPage />} />
        <Route path="/master-key/approve" element={<LazyMasterKeyApprovePage />} />
        <Route path="/master-key/conflict" element={<LazyMasterKeyConflictPage />} />
        <Route path="/profile-ready" element={<LazyProfileReadyPage />} />
        <Route path="/connect-profile" element={<LazyConnectProfilePage />} />
        <Route path="/connect-profile/import" element={<LazyConnectProfileImportPage />} />
        <Route path="/connect-profile/device" element={<LazyConnectProfileDevicePage />} />
        <Route path="/add-device" element={<LazyAddDeviceFlowPage />} />
        <Route path="/temporary-access" element={<LazyTemporaryAccessPage />} />
        <Route path="/temporary-access/:enrollmentId" element={<LazyTemporaryCirclePage />} />
        <Route path="/temporary-access/:enrollmentId/chat/:chatId" element={<LazyTemporaryGroupChatPage />} />

        {/* Invite deep-link (prefills server + token) */}
        <Route path="/invite" element={<LazyAddServerPage />} />
        <Route path="/invite/:token" element={<LazyAddServerPage />} />
        <Route path="/call-link/:linkId" element={<LazyCallLinkPage />} />
        <Route path="/guest-link" element={<LazyDirectGuestLinkPage />} />
        <Route path="/group-invite" element={<LazyGroupInviteLinkPage />} />
        <Route path="/open" element={<LazyOpenLinkPage />} />
        <Route path="/scan" element={<LazyUniversalQrScannerPage />} />

        {/* Key management is accessible both ways */}
        <Route path="/keys" element={<KeyManagementWorkspaceRoute />} />
        <Route path="/import" element={<KeyManagementWorkspaceRoute />} />
        <Route
          path="/message-archives"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="settings"
                master={<LazySettingsPage />}
                mobile={<LazyMessageArchiveManagementPage />}
                detail={<LazyMessageArchiveManagementPage />}
                emptyTitle={t('workspace.settingsEmptyTitle')}
                emptyDescription={t('workspace.settingsEmptyDescription')}
              />
            </PrivateRoute>
          }
        />

        {/* Identity recovery (accessible from add-server flow) */}
        <Route path="/recover-identity" element={<LazyRecoverIdentityPage />} />

        {/* Main navigation routes */}
        <Route
          path="/calls"
          element={
            <PrivateRoute>
              <CallsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/contacts"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="contacts"
                master={<LazyContactsPage />}
                mobile={<LazyContactsPage />}
                emptyTitle={t('workspace.contactsEmptyTitle')}
                emptyDescription={t('workspace.contactsEmptyDescription')}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/contacts/invite"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="contacts"
                master={<LazyContactsPage />}
                mobile={<LazyInviteCirclesPage />}
                detail={<LazyInviteCirclesPage />}
                emptyTitle={t('workspace.contactsEmptyTitle')}
                emptyDescription={t('workspace.contactsEmptyDescription')}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/messages"
          element={
            <PrivateRoute>
              <MessagesWorkspaceRoute mobile={<LazyMessagesPage />} />
            </PrivateRoute>
          }
        />
        <Route
          path="/messages/new"
          element={
            <PrivateRoute>
              <MessagesWorkspaceRoute mobile={<LazyNewChatPage />} detail={<LazyNewChatPage />} />
            </PrivateRoute>
          }
        />
        <Route
          path="/share-target"
          element={
            <PrivateRoute>
              <LazyShareTargetPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/messages/:localIdentityId/:peerId"
          element={
            <PrivateRoute>
              <MessagesWorkspaceRoute mobile={<LazyMessageThreadPage />} detail={<LazyMessageThreadPage />} />
            </PrivateRoute>
          }
        />
        <Route
          path="/messages/group/:chatId"
          element={
            <PrivateRoute>
              <MessagesWorkspaceRoute mobile={<LazyGroupThreadPage />} detail={<LazyGroupThreadPage />} />
            </PrivateRoute>
          }
        />
        <Route
          path="/messages/group/:localIdentityId/:chatId"
          element={
            <PrivateRoute>
              <MessagesWorkspaceRoute mobile={<LazyGroupThreadPage />} detail={<LazyGroupThreadPage />} />
            </PrivateRoute>
          }
        />
        <Route
          path="/contacts/:contactId"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="contacts"
                master={<LazyContactsPage />}
                mobile={<LazyContactDetailPage />}
                detail={<LazyContactDetailPage />}
                emptyTitle={t('workspace.contactsEmptyTitle')}
                emptyDescription={t('workspace.contactsEmptyDescription')}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/contacts/add"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="contacts"
                master={<LazyContactsPage />}
                mobile={<LazyAddContactPage />}
                detail={<LazyAddContactPage />}
                emptyTitle={t('workspace.contactsEmptyTitle')}
                emptyDescription={t('workspace.contactsEmptyDescription')}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/share-contact"
          element={
            <PrivateRoute>
              <LazyShareContactPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/servers"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="servers"
                master={<LazyServersPage />}
                mobile={<LazyServersPage />}
                emptyTitle={t('workspace.serversEmptyTitle')}
                emptyDescription={t('workspace.serversEmptyDescription')}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/servers/create"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="servers"
                master={<LazyServersPage />}
                mobile={<LazyCreateCircleEntryPage />}
                detail={<LazyCreateCircleEntryPage />}
                emptyTitle={t('workspace.serversEmptyTitle')}
                emptyDescription={t('workspace.serversEmptyDescription')}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/servers/create/configure"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="servers"
                master={<LazyServersPage />}
                mobile={<LazyCreateCirclePage />}
                detail={<LazyCreateCirclePage />}
                emptyTitle={t('workspace.serversEmptyTitle')}
                emptyDescription={t('workspace.serversEmptyDescription')}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/servers/:identityId/guest-links"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="servers"
                master={<LazyServersPage />}
                mobile={<LazyGuestLinksPage />}
                detail={<LazyGuestLinksPage />}
                emptyTitle={t('workspace.serversEmptyTitle')}
                emptyDescription={t('workspace.serversEmptyDescription')}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/servers/:identityId/circle-settings"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="servers"
                master={<LazyServersPage />}
                mobile={<LazyCircleSettingsPage />}
                detail={<LazyCircleSettingsPage />}
                emptyTitle={t('workspace.serversEmptyTitle')}
                emptyDescription={t('workspace.serversEmptyDescription')}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/servers/:identityId/archive"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="servers"
                master={<LazyServersPage />}
                mobile={<LazyMessageArchivePage />}
                detail={<LazyMessageArchivePage />}
                emptyTitle={t('workspace.serversEmptyTitle')}
                emptyDescription={t('workspace.serversEmptyDescription')}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/servers/:identityId/:section"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="servers"
                master={<LazyServersPage />}
                mobile={<LazyServerDetailPage />}
                detail={<LazyServerDetailPage />}
                emptyTitle={t('workspace.serversEmptyTitle')}
                emptyDescription={t('workspace.serversEmptyDescription')}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/servers/:identityId"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="servers"
                master={<LazyServersPage />}
                mobile={<LazyServerDetailPage />}
                detail={<LazyServerDetailPage />}
                emptyTitle={t('workspace.serversEmptyTitle')}
                emptyDescription={t('workspace.serversEmptyDescription')}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="settings"
                master={<LazySettingsPage />}
                mobile={<LazySettingsPage />}
                emptyTitle={t('workspace.settingsEmptyTitle')}
                emptyDescription={t('workspace.settingsEmptyDescription')}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/server-management"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="settings"
                master={<LazySettingsPage />}
                mobile={<LazyServerManagementPage />}
                detail={<LazyServerManagementPage />}
                emptyTitle={t('workspace.settingsEmptyTitle')}
                emptyDescription={t('workspace.settingsEmptyDescription')}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/message-archives"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="settings"
                master={<LazySettingsPage />}
                mobile={<LazyMessageArchiveManagementPage />}
                detail={<LazyMessageArchiveManagementPage />}
                emptyTitle={t('workspace.settingsEmptyTitle')}
                emptyDescription={t('workspace.settingsEmptyDescription')}
              />
            </PrivateRoute>
          }
        />

        {/* Settings sub-pages */}
        <Route
          path="/settings/status"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="settings"
                master={<LazySettingsPage />}
                mobile={<LazyMyStatusPage />}
                detail={<LazyMyStatusPage />}
                emptyTitle={t('workspace.settingsEmptyTitle')}
                emptyDescription={t('workspace.settingsEmptyDescription')}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/devices"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="settings"
                master={<LazySettingsPage />}
                mobile={<LazyDeviceManagementPage />}
                detail={<LazyDeviceManagementPage />}
                emptyTitle={t('workspace.settingsEmptyTitle')}
                emptyDescription={t('workspace.settingsEmptyDescription')}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/advanced"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="settings"
                master={<LazySettingsPage />}
                mobile={<LazyAdvancedSettingsPage />}
                detail={<LazyAdvancedSettingsPage />}
                emptyTitle={t('workspace.settingsEmptyTitle')}
                emptyDescription={t('workspace.settingsEmptyDescription')}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/chat"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="settings"
                master={<LazySettingsPage />}
                mobile={<LazyChatSettingsPage />}
                detail={<LazyChatSettingsPage />}
                emptyTitle={t('workspace.settingsEmptyTitle')}
                emptyDescription={t('workspace.settingsEmptyDescription')}
              />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings/trusted-access-requests"
          element={
            <PrivateRoute>
              <SectionWorkspaceRoute
                section="settings"
                master={<LazySettingsPage />}
                mobile={<LazyTemporaryTrustedAccessRequestsPage />}
                detail={<LazyTemporaryTrustedAccessRequestsPage />}
                emptyTitle={t('workspace.settingsEmptyTitle')}
                emptyDescription={t('workspace.settingsEmptyDescription')}
              />
            </PrivateRoute>
          }
        />

        {/* Call page */}
        <Route
          path="/call"
          element={isWebViewMode ? <Navigate to="/messages" replace /> : <LazyCallPage />}
        />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/messages" />} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/messages" />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
